import React from 'react';
import PropTypes from 'prop-types';
import { analytics } from '@sendgrid/bloodhound';
import queryString from 'query-string';
import { Button } from '@sendgrid/ui-components/button';
import { TextInput } from '@sendgrid/ui-components/text-input';
import { Checkbox } from '@sendgrid/ui-components/checkbox';
import {
  validateUsername, validatePassword, validateEmail, validateConfirmPassword,
} from '../utils/formInput';
import ERRORTYPES from '../constants/errorTypes';
import createRequest from '../utils/requestService';
import Styles from './resellerForm.module.scss';
import getEnvVariable from '../utils/environment';
import { ENVIRONMENT, APPINDICATORS } from '../constants';
import { trackOnce } from '../utils/Analytics';
import { GoogleSignupEvents } from '../analytics/googleSignupEvents';
import validationRules from '../constants/validationRules';

export default class ResellerForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      formInputs: {
        username: '',
        password: '',
        passwordConfirm: '',
        email: '',
      },
      formValidity: {
        // - usernameIsValid will be updated by two different funtions,
        //    one checks if the username exists and one checks that the username meets requirements
        // - These are defaulted to true so that on load, the inputs do not have an !isValid state
        //    making them red
        usernameIsValid: true,
        passwordIsValid: true,
        passwordConfirmIsValid: true,
        emailIsValid: true,
      },
      formErrors: {
        usernameError: '',
        passwordError: '',
        passwordConfirmError: '',
        emailError: '',
      },
      isTosAcknowledged: false,
    };
    this.trackUsernameComplete = trackOnce();
    this.trackPasswordCompleted = trackOnce();
    this.trackConfirmPasswordCompleted = trackOnce();
    this.trackEmailAddressCompleted = trackOnce();
    this.trackTermsOfServiceAccepted = trackOnce();
  }

  handleChange = (e) => {
    const property = e.target.getAttribute('id');
    const { value } = e.target;
    if (property === 'username') {
      this.usernameValidation(value);
    }
    if (property === 'password') {
      this.passwordValidation(value);
    }
    if (property === 'email') {
      this.emailValidation(value);
    }
    if (property === 'passwordConfirm') {
      this.passwordConfirmValidation(value);
    }
    this.setState({
      formInputs: {
        ...this.state.formInputs,
        [property]: value,
      },
    });
  };

  handleBlur = (e) => {
    const property = e.target.getAttribute('id');
    const { value } = e.target;
    if (property === 'username') {
      if (validateUsername(value).isValid) {
        this.usernameExistsCheck();
      }
    }
    if (property === 'password') {
      if (validatePassword(value).isValid) {
        this.trackPasswordCompleted(GoogleSignupEvents.passwordCompleted());
      }
    }
    if (property === 'email') {
      if (validateEmail(value).isValid) {
        this.trackEmailAddressCompleted(GoogleSignupEvents.emailAddressCompleted());
      }
    }
    if (property === 'passwordConfirm') {
      if (validateConfirmPassword(value, this.state.formInputs.password).isValid) {
        this.trackConfirmPasswordCompleted(GoogleSignupEvents.confirmPasswordCompleted());
      }
    }
  };

  handleCheck = (e) => {
    const { checked } = e.target;
    if (checked) {
      this.trackTermsOfServiceAccepted(GoogleSignupEvents.termsOfServiceAccepted());
    }
    this.setState({
      isTosAcknowledged: checked,
    });
  };

  inputsAreValid = () => {
    const { formValidity } = this.state;
    const isValid = value => formValidity[value] === true;
    return Object.keys(formValidity).every(isValid);
  };

  inputsAreNotEmpty = () => {
    const { formInputs } = this.state;
    const isNotEmpty = input => formInputs[input] !== '';
    return Object.keys(formInputs).every(isNotEmpty);
  };

  usernameValidation = (username) => {
    const response = validateUsername(username);
    this.setState({
      formValidity: {
        ...this.state.formValidity,
        usernameIsValid: response.isValid,
      },
      formErrors: {
        ...this.state.formErrors,
        usernameError: response.errorType,
      },
    });
  }

  usernameExistsCheck = () => {
    if (!this.state.formValidity.usernameIsValid) {
      return this.setState({
        formErrors: {
          ...this.state.formErrors,
          usernameError: `${ERRORTYPES.USERNAME_PREFIX}${ERRORTYPES.USERNAME_DEFAULT}`,
        },
      });
    }
    return fetch(createRequest(
      `/v3/public/signup/username/${this.state.formInputs.username}`,
      { method: 'GET' },
    ))
      .then((response) => {
        switch (response.status) {
          case 404:
            this.trackUsernameComplete(GoogleSignupEvents.usernameCompleted());
            return {
              isValid: true,
              errorType: ERRORTYPES.USERNAME_AVAILABLE,
            };

          case 429:
            return {
              isValid: false,
              errorType: ERRORTYPES.USERNAME_TOO_MANY_REQUESTS,
            };

          case 204:
            return {
              isValid: false,
              errorType: ERRORTYPES.USERNAME_TAKEN,
            };
          case 400:
            return {
              isValid: false,
              errorType: ERRORTYPES.USERNAME_INVALID,
            };

          default:
            return {
              isValid: false,
              errorType: ERRORTYPES.USERNAME_DEFAULT,
            };
        }
      })
      .then((response) => {
        this.setState({
          formValidity: {
            ...this.state.formValidity,
            usernameIsValid: response.isValid,
          },
          formErrors: {
            ...this.state.formErrors,
            usernameError: response.errorType,
          },
        });
        return response;
      }).catch((error) => {
        // This call doesn't break the experience, so we don't need to notify the user
        this.setState({
          formValidity: {
            ...this.state.formValidity,
            usernameIsValid: true,
          },
          formErrors: {
            ...this.state.formErrors,
            usernameError: ERRORTYPES.USERNAME_AVAILABLE,
          },
        });
        // eslint-disable-next-line
         console.log(error);
      });
  };

  passwordValidation = (password) => {
    const response = validatePassword(
      password,
      this.state.formInputs.passwordConfirm,
      this.state.username,
    );
    this.setState({
      formValidity: {
        ...this.state.formValidity,
        passwordIsValid: response.isValid,
        passwordConfirmIsValid: response.confirmPassword_isValid,
      },
      formErrors: {
        ...this.state.formErrors,
        passwordError: response.errorType,
        passwordConfirmError: response.confirmPassword_errorType,
      },
    });
  };

  passwordConfirmValidation = (password) => {
    const response = validateConfirmPassword(password, this.state.formInputs.password);
    this.setState({
      formValidity: {
        ...this.state.formValidity,
        passwordConfirmIsValid: response.isValid,
      },
      formErrors: {
        ...this.state.formErrors,
        passwordConfirmError: response.errorType,
      },
    });
  };

  emailValidation = (email) => {
    const response = validateEmail(email);
    this.setState({
      formValidity: {
        ...this.state.formValidity,
        emailIsValid: response.isValid,
      },
      formErrors: {
        ...this.state.formErrors,
        emailError: response.errorType,
      },
    });
  };

  acknowledgeTos = () => {
    const handleTosClick = () => {
      analytics.track(GoogleSignupEvents.termsOfServiceClicked());
    };

    const handlePrivacyPolicyClick = () => {
      analytics.track(GoogleSignupEvents.privacyPolicyClicked());
    };

    return (
      <div className={Styles['reseller-form__tos']} data-role="tos-container">
        <Checkbox
          onChange={this.handleCheck}
          id="reseller-checkbox"
          data-role="checkbox"
        >
            I accept the
          {' '}
          <a
            target="_blank"
            rel="noopener noreferrer"
            href="https://sendgrid.com/policies/tos/"
            data-role="tos-link"
            onClick={handleTosClick}
          >
              Terms of Service
          </a>
          {' '}
  and have read the
          {' '}
          <a
            target="_blank"
            rel="noopener noreferrer"
            href="https://sendgrid.com/policies/privacy/services-privacy-policy/"
            data-role="privacy-policy-link"
            onClick={handlePrivacyPolicyClick}
          >
              Services Privacy Policy
          </a>
        </Checkbox>
      </div>
    );
  };

  createUser = () => {
    const payload = {
      username: this.state.formInputs.username,
      password: this.state.formInputs.password,
      email: this.state.formInputs.email,
      token: queryString.parse(this.props.location.search).token,
    };
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ...payload }),
    };
    this.props.setIsLoading(true);
    return (
      fetch(`${getEnvVariable(ENVIRONMENT.GOOGLE_RESELLER)}/v2/public/reseller/google/create`, options)
        .then((response) => {
          if (!response.ok) {
            analytics.track(GoogleSignupEvents.accountCreated());
            this.props.setCreateUserError(APPINDICATORS.ALERT_TEXT.USER_CREATION_ERROR);
            throw new Error('Failed to create user');
          }
          return this.props.showSuccessMessage();
        })
        // eslint-disable-next-line
        .catch(error => console.log(error.message))
        .finally(() => this.props.setIsLoading(false))
    );
  }

  render() {
    const {
      usernameIsValid, passwordIsValid, passwordConfirmIsValid, emailIsValid,
    } = this.state.formValidity;
    const areInputsValid = this.inputsAreValid();
    const areInputsEmpty = !this.inputsAreNotEmpty();
    return (
      <React.Fragment>
        <p className="signupSubtitle">Create a new Twilio SendGrid account</p>
        <form className={Styles['reseller-form']}>
          <TextInput
            type="text"
            label="Username"
            isRequired
            isValid={usernameIsValid}
            id="username"
            info={this.state.formErrors.usernameError}
            onChange={this.handleChange}
            onBlur={this.handleBlur}
            data-field="userName"
            value={this.state.formInputs.username}
            data-role="username-field"
          />
          <TextInput
            type="password"
            label="Password"
            isRequired
            isValid={passwordIsValid}
            id="password"
            info={
              this.state.formErrors.passwordError === ''
                ? `Must have at least ${validationRules.PASSWORD_LENGTH_MIN} characters, including at least 1 letter and number.`
                : this.state.formErrors.passwordError
            }
            onChange={this.handleChange}
            onBlur={this.handleBlur}
            value={this.state.formInputs.password}
            data-role="password-field"
          />
          <TextInput
            type="password"
            label="Confirm Password"
            isRequired
            isValid={passwordConfirmIsValid}
            id="passwordConfirm"
            info={this.state.formErrors.passwordConfirmError}
            onChange={this.handleChange}
            onBlur={this.handleBlur}
            value={this.state.formInputs.passwordConfirm}
            data-role="confirm-password-field"
          />
          <TextInput
            type="email"
            label="Email Address"
            isRequired
            isValid={emailIsValid}
            id="email"
            info={this.state.formErrors.emailError}
            onChange={this.handleChange}
            onBlur={this.handleBlur}
            value={this.state.formInputs.email}
            data-role="email-field"
          />
          {this.acknowledgeTos()}
          <center>
            <Button
              disabled={
              areInputsEmpty || !areInputsValid || !this.state.isTosAcknowledged
            }
              onClick={this.createUser}
            >
              Continue
            </Button>
          </center>
        </form>
      </React.Fragment>
    );
  }
}

ResellerForm.propTypes = {
  showSuccessMessage: PropTypes.func.isRequired,
  location: PropTypes.shape({
    search: PropTypes.string.isRequired,
  }),
  setCreateUserError: PropTypes.func.isRequired,
  setIsLoading: PropTypes.func.isRequired,
};

ResellerForm.defaultProps = {
  location: {
    search: '',
  },
};
